import type { ComponentProps, ReactNode } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import TooltipIcon from "@/v2-ui/tooltip/TooltipIcon"

export type UsageReportSectionProps = Omit<
  ComponentProps<"section">,
  "title"
> & {
  /**
   * Small uppercase label over the title that names the chapter ("USAGE",
   * "CUSTOMERS"), the way the report's pages open. Sections only.
   */
  eyebrow?: ReactNode
  /** Sentence-case title: what the section is about ("What they use it for"). */
  title: ReactNode
  /**
   * One or two plain sentences that say what the reader is looking at and
   * how to read it. Written for a sales or CS colleague, not an engineer:
   * no table names, no "ISO", no "UTC".
   */
  lead?: ReactNode
  /** Longer methodology behind a small info icon next to the title. */
  info?: string
  /** Controls on the header's right: a search box, a badge row, a switch. */
  aside?: ReactNode
  /** A smaller title for a section nested inside another. */
  level?: "section" | "subsection"
}

/**
 * The page unit every usage surface is built from: an eyebrow, a title, a
 * lead paragraph, and the content under it — grouped into cards by the
 * caller. Sections are separated by air, not rules, so the eye reads one
 * topic at a time the way it reads the report.
 */
function UsageReportSection(props: UsageReportSectionProps) {
  const {
    eyebrow,
    title,
    lead,
    info,
    aside,
    level = "section",
    className,
    children,
    ...sectionProps
  } = props

  const heading = (
    <span className="flex items-center gap-1.5">
      {title}
      {info && (
        <TooltipIcon
          iconSrc="info"
          iconSize={13}
          iconClassName="shrink-0 text-muted-foreground/70"
          content={
            <span className="block max-w-[320px] px-3 py-2 text-xs font-normal leading-relaxed">
              {info}
            </span>
          }
          contentSide="top"
          contentClassName="ui-tooltip-content--panel"
        />
      )}
    </span>
  )

  return (
    <section
      className={cn(
        "usage-report-section",
        level === "subsection" && "usage-report-section--sub",
        className,
      )}
      {...sectionProps}
    >
      <header className="usage-report-section-header">
        <div className="usage-report-section-heading">
          {eyebrow != null && level === "section" && (
            <span className="usage-report-eyebrow">{eyebrow}</span>
          )}
          {level === "section" ? (
            <h3 className="usage-report-title">{heading}</h3>
          ) : (
            <h4 className="usage-report-title">{heading}</h4>
          )}
          {lead != null && <p className="usage-report-lead">{lead}</p>}
        </div>
        {aside != null && (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {aside}
          </div>
        )}
      </header>
      {children}
    </section>
  )
}

export default UsageReportSection
